import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import useInboxRealtime from './useInboxRealtime';

import useMessages from './useMessages';

const MessagesStateContext = createContext(null);
const MessagesActionsContext = createContext(null);

function MessagesProvider({
  conversationId, readProfileId = null, onConversationUpdate, onMarkRead, sendOverride, initialComposerState, children,
}) {
  const viewerProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // Messages are stored per receipt-holder, so a read filters on receiver_profile_id. That is the
  // viewer everywhere except the request page viewed by staff, where the thread belongs to the
  // reporter: filtering on the viewer there matches no receipts and the thread reads as empty.
  // readProfileId lets that caller name whose receipts to read; RLS still decides who may.
  const currentReceiverProfileId = readProfileId || viewerProfileId;

  const {
    messages,
    messageContent,
    attachments,
    setAttachments,
    videoRequestIntent,
    setVideoRequestIntent,
    sendState,
    isLoading,
    isLoadingOlder,
    hasMoreOlder,
    loadOlder,
    pendingScrollRef,
    setMessageContent,
    sendMessage,
    refetchMessages,
    syncLatestMessages,
  } = useMessages(conversationId, currentReceiverProfileId, onConversationUpdate, sendOverride, initialComposerState);

  // Inbox-wide realtime: refreshes conversations list for any new message, and merges the
  // newest messages into the active conversation without dropping loaded older pages.
  // Realtime stays on the viewer's own channel: it is "a message arrived for me", and a staff
  // reader has no receipts on this thread so nothing would fire for them anyway.
  useInboxRealtime(viewerProfileId, conversationId, {
    onActiveConversationMessage: syncLatestMessages,
    onAnyConversationMessage: onConversationUpdate,
  });

  const state = useMemo(
    () => ({
      messages,
messageContent,
attachments,
videoRequestIntent,
sendState,
isLoading,
      isLoadingOlder,
hasMoreOlder,
pendingScrollRef,
    }),
    [messages, messageContent, attachments, videoRequestIntent, sendState, isLoading,
      isLoadingOlder, hasMoreOlder, pendingScrollRef],
  );

  const actions = useMemo(
    () => ({
      setMessageContent, setAttachments, setVideoRequestIntent, sendMessage, refetchMessages, loadOlder, onMarkRead,
    }),
    [setMessageContent, setAttachments, setVideoRequestIntent, sendMessage, refetchMessages, loadOlder, onMarkRead],
  );

  return (
    <MessagesStateContext.Provider value={state}>
      <MessagesActionsContext.Provider value={actions}>{children}</MessagesActionsContext.Provider>
    </MessagesStateContext.Provider>
  );
}

MessagesProvider.propTypes = {
  conversationId: PropTypes.string,
  // Whose receipts to read the thread under; defaults to the viewer's own profile
  readProfileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onConversationUpdate: PropTypes.func,
  onMarkRead: PropTypes.func,
  sendOverride: PropTypes.func,
  // Seed for fresh mounts (quick replies prefill); inbox callers omit it
  initialComposerState: PropTypes.shape({
    messageContent: PropTypes.string,
    videoRequestIntent: PropTypes.bool,
  }),
  children: PropTypes.node.isRequired,
};

function useMessagesState() {
  const context = useContext(MessagesStateContext);
  if (!context) {
    throw new Error('useMessagesState must be used within a MessagesProvider');
  }
  return context;
}

function useMessagesActions() {
  const context = useContext(MessagesActionsContext);
  if (!context) {
    throw new Error('useMessagesActions must be used within a MessagesProvider');
  }
  return context;
}

export { MessagesProvider, useMessagesState, useMessagesActions };
